// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// commands.ts — the command log: a flat array of integers, four per command, in canonical order.
//
// ONE COMMAND PER UNIT, deliberately. The obvious encoding is a selection bitmask — "move these
// eleven units to tile 87" in one thirty-two-bit payload — and it is a trap, because a mask has to
// index something. Global entity ids overflow it after thirty builds, and a mask over "your units
// alive right now, sorted" changes meaning if a unit dies during the three hundred milliseconds of
// input delay, so the order arrives meaning something the player did not ask for. Eleven separate
// four-integer commands cost about two hundred bytes and cannot be misinterpreted.
//
// The flat shape also makes the merge trivial, which is what the netcode is built on: every message
// carries the WHOLE log, the receiver concatenates and re-canonicalises, and identical commands
// collapse. A dropped datagram is then not an event at all — the next one reconstructs the world —
// and the classic lockstep hang, where one lost order leaves both peers waiting on each other for
// ever, cannot happen because there is no delta to lose.

import { CMD_WORDS, Cmd } from './sim';

export interface Command {
  tick: number;
  seat: number;
  kind: Cmd;
  /** The unit this order is for, or −1 for a side-wide order (build, rally). */
  unit: number;
  arg: number;
}

export const pack = (c: Command): number[] => [c.tick, (c.seat << 4) | c.kind, c.unit, c.arg];

export function unpack(log: readonly number[], i: number): Command {
  return {
    tick: log[i],
    seat: (log[i + 1] >> 4) & 15,
    kind: (log[i + 1] & 15) as Cmd,
    unit: log[i + 2],
    arg: log[i + 3],
  };
}

export const commandCount = (log: readonly number[]): number => (log.length / CMD_WORDS) | 0;

/**
 * Sort and de-duplicate.
 *
 * The order is total — (tick, word, unit, arg) can never tie for two DIFFERENT commands, because two
 * commands equal on all four ARE the same command. That matters more than it looks: an unstable
 * comparator here would let two peers apply the same tick's orders in different sequences, and since
 * a build spends ore and a move re-paths, "the same orders in a different order" is a live desync
 * rather than a cosmetic difference.
 */
export function canonical(log: readonly number[]): number[] {
  const n = (log.length / CMD_WORDS) | 0;
  const rows: number[][] = [];
  for (let i = 0; i < n; i++) rows.push(log.slice(i * CMD_WORDS, i * CMD_WORDS + CMD_WORDS));
  rows.sort((a, b) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2] || a[3] - b[3]);
  const out: number[] = [];
  let last: number[] | null = null;
  for (const r of rows) {
    if (last && last[0] === r[0] && last[1] === r[1] && last[2] === r[2] && last[3] === r[3]) continue;
    out.push(r[0], r[1], r[2], r[3]);
    last = r;
  }
  return out;
}

/** True when `merged` says nothing `mine` did not already say — the common case for a heartbeat. */
export const sameLog = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * The earliest tick at which `next` disagrees with `prev`, or −1 if `next` is a pure extension.
 *
 * This is what decides between "keep going" and "re-derive". A command that lands for a tick we
 * have already simulated has to be replayed from before that tick, or the two peers are running
 * different histories from the same log — which is exactly the failure the digest would catch three
 * seconds later, as a desync banner, instead of being fixed silently here.
 */
export function firstDivergence(prev: readonly number[], next: readonly number[]): number {
  for (let i = 0; i + CMD_WORDS <= next.length; i += CMD_WORDS) {
    if (i + CMD_WORDS > prev.length) return next[i];
    for (let w = 0; w < CMD_WORDS; w++) if (prev[i + w] !== next[i + w]) return Math.min(prev[i], next[i]);
  }
  return -1;
}
